import React, { useEffect, useState, useRef } from 'react';
import { Menu, Search, Bell, User, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  clearAdminSession,
  AHG_ADMIN_PROFILE_SESSION,
} from '../../lib/api';

interface AdminTopbarProps {
  onMenuClick: () => void;
}

export function AdminTopbar({ onMenuClick }: AdminTopbarProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const userEmail = (() => {
    try {
      const raw = sessionStorage.getItem(AHG_ADMIN_PROFILE_SESSION);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { email?: string };
      return parsed?.email ?? null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    clearAdminSession();
    setMenuOpen(false);
    navigate('/admin/login', { replace: true });
  };

  return (
    <header className="h-16 bg-white border-b border-sea-blue/10 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="md:hidden text-sea-blue/85 hover:text-sea-blue p-1"
          aria-label="Open menu">
          <Menu size={24} />
        </button>

        <div className="hidden sm:block text-sm font-medium text-sea-blue/50">
          Admin Dashboard
        </div>
      </div>

      <div className="flex items-center gap-4 sm:gap-6">
        <div className="relative hidden md:block w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sea-blue/40">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Search places, users..."
            className="w-full bg-sand/30 border border-sea-blue/10 rounded-full py-1.5 pl-9 pr-4 text-sm text-sea-blue placeholder:text-sea-blue/40 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
          />
        </div>

        <button
          className="relative text-sea-blue/80 hover:text-sea-blue transition-colors p-1"
          aria-label="Notifications">
          <Bell size={20} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-terracotta rounded-full border border-white"></span>
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 text-sea-blue/80 hover:text-sea-blue transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Account menu">
            <div className="w-8 h-8 rounded-full bg-sand flex items-center justify-center border border-sea-blue/10">
              <User size={16} />
            </div>
          </button>

          {menuOpen ?
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-float border border-sea-blue/10 py-2 z-40 overflow-hidden">
              <div className="px-4 py-3 border-b border-sea-blue/5">
                <div className="text-xs uppercase tracking-wider text-sea-blue/40 font-bold mb-1">
                  Signed in as
                </div>
                <div className="text-sm font-medium text-sea-blue truncate">
                  {userEmail || 'Admin'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/settings');
                }}
                className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-sea-blue hover:bg-sand/50 transition-colors"
                role="menuitem">
                <Settings size={16} className="text-sea-blue/80" />
                Settings
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-terracotta hover:bg-terracotta/5 transition-colors"
                role="menuitem">
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          : null}
        </div>
      </div>
    </header>
  );
}
